import { useEffect, useMemo, useState } from 'react';
import type { Comment } from '../api/client';
import { ARTIFACT_ORIGIN } from '../env';

interface UseResolvedCommentAnchorsArgs {
  iframeLoaded: boolean;
  openComments: Comment[];
  currentVersion: number;
  postToIframe: (message: Record<string, unknown>) => void;
}

export type ResolvedMarkerPosition = { x: number; y: number };

export type IframeScrollState = {
  scrollY: number;
  scrollHeight: number;
  viewportHeight: number;
};

type MarkerAnchorPayload = {
  id: string;
  type: 'text' | 'element' | 'point';
  initial: string;
  tooltip: string;
  authorName: string;
  xpath?: string;
  text?: string;
  prefix?: string;
  suffix?: string;
  startOffset?: number;
  endOffset?: number;
  label?: string;
  tagName?: string;
  fingerprint?: string;
  rect?: { x: number; y: number; width: number; height: number };
  x?: number;
  y?: number;
};

function buildPayload(comment: Comment): MarkerAnchorPayload | null {
  if (!comment.anchor) return null;
  const initial = comment.author.name.trim().charAt(0).toUpperCase() || '?';
  const tooltipText = `${comment.author.name}: ${comment.body.slice(0, 60)}`;
  const base = { id: comment.id, initial, tooltip: tooltipText, authorName: comment.author.name };

  if (comment.anchor.type === 'text') {
    return {
      ...base,
      type: 'text',
      xpath: comment.anchor.xpath,
      text: comment.anchor.text,
      prefix: comment.anchor.prefix,
      suffix: comment.anchor.suffix,
      startOffset: comment.anchor.startOffset,
      endOffset: comment.anchor.endOffset,
    };
  }
  if (comment.anchor.type === 'element') {
    return {
      ...base,
      type: 'element',
      xpath: comment.anchor.xpath,
      label: comment.anchor.label,
      text: comment.anchor.text,
      tagName: comment.anchor.tagName,
      fingerprint: comment.anchor.fingerprint,
      rect: comment.anchor.rect,
    };
  }
  if (comment.anchor.type === 'point') {
    return { ...base, type: 'point', x: comment.anchor.x, y: comment.anchor.y };
  }
  return null;
}

export function useResolvedCommentAnchors({
  iframeLoaded,
  openComments,
  currentVersion,
  postToIframe,
}: UseResolvedCommentAnchorsArgs) {
  const [resolvedPositions, setResolvedPositions] = useState<Record<string, ResolvedMarkerPosition>>({});
  const [hasResolverResponded, setHasResolverResponded] = useState(false);
  const [iframeScroll, setIframeScroll] = useState<IframeScrollState>({
    scrollY: 0,
    scrollHeight: 0,
    viewportHeight: 1,
  });

  const payloads = useMemo(() => {
    const out: MarkerAnchorPayload[] = [];
    for (const comment of openComments) {
      if (comment.parent_id || comment.resolved_at) continue;
      if (comment.version_number !== currentVersion) continue;
      const payload = buildPayload(comment);
      if (payload) out.push(payload);
    }
    return out;
  }, [openComments, currentVersion]);

  const payloadSignature = useMemo(() => JSON.stringify(payloads), [payloads]);

  useEffect(() => {
    if (!iframeLoaded) {
      setResolvedPositions({});
      setHasResolverResponded(false);
      return;
    }
    postToIframe({ type: 'exhibit:resolve-anchors', payload: { anchors: payloads } });
    // Intentionally keyed on payloadSignature to avoid re-posting identical lists.
    // Scroll-driven re-resolution happens inside the iframe wrapper itself
    // (queueReconcile on its own scroll listener), so we don't re-post here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [iframeLoaded, payloadSignature, postToIframe]);

  useEffect(() => {
    if (!iframeLoaded) return;
    const timers = [
      window.setTimeout(() => postToIframe({ type: 'exhibit:resolve-anchors', payload: { anchors: payloads } }), 250),
      window.setTimeout(() => postToIframe({ type: 'exhibit:resolve-anchors', payload: { anchors: payloads } }), 1000),
      window.setTimeout(() => postToIframe({ type: 'exhibit:resolve-anchors', payload: { anchors: payloads } }), 2500),
    ];
    return () => timers.forEach((timer) => window.clearTimeout(timer));
    // Intentionally keyed on payloadSignature to avoid scheduling retries for
    // referential-only payload changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [iframeLoaded, payloadSignature, postToIframe]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (ARTIFACT_ORIGIN && event.origin !== ARTIFACT_ORIGIN) return;
      const { type, payload } = event.data || {};

      if (type === 'exhibit:anchors-resolved') {
        const next: Record<string, ResolvedMarkerPosition> = {};
        for (const item of payload?.resolved ?? []) {
          if (item?.id && item.pos) next[item.id] = item.pos;
        }
        setResolvedPositions(next);
        setHasResolverResponded(true);
      } else if (type === 'exhibit:scroll') {
        setIframeScroll({
          scrollY: Number(payload?.scrollY ?? 0),
          scrollHeight: Number(payload?.scrollHeight ?? 0),
          viewportHeight: Number(payload?.viewportHeight ?? 1) || 1,
        });
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  return { resolvedPositions, hasResolverResponded, iframeScroll };
}
